import {
  Asset,
  AssetKind,
  AssetFile,
  AssetPreview,
  AssetSummary,
  AssetType,
  EnvironmentAsset,
  GetResolutionLabelParams,
  GraphicsAsset,
  MaterialsAsset,
  MiscAsset,
  Resolution,
  ResolutionId,
  ResolutionLabel,
  Resolutions,
  SoundsAsset,
  ThreeDAsset,
  TutorialAsset,
  VideoAsset,
} from '@/lib/assets/types'
import { baseURL, CRATE_CORE_API_BASE_URL } from '@/lib/config'
import { ASSET_URL_TYPES } from '@/lib/constants'

// export const isFootageCrateEffect = (asset: { __typename: AssetType }): asset is FootageCrateEffect => {
//   return asset.__typename === 'fc_effects'
// }

export const isFootageCrateEffect = (asset: { vertical: AssetKind }): asset is VideoAsset => {
  return asset.vertical === 'videos'
}

export const isGraphicsCrateImage = (asset: { vertical: AssetKind }): asset is GraphicsAsset => {
  return asset.vertical === 'graphics'
}

export const isSoundsCrateSound = (asset: { vertical: AssetKind }): asset is SoundsAsset => {
  return asset.vertical === 'sounds'
}

export const isRenderCrateEnvironment = (asset: { vertical: AssetKind }): asset is EnvironmentAsset => {
  return asset.vertical === 'environments'
}

export const isRenderCrateMaterial = (asset: { vertical: AssetKind }): asset is MaterialsAsset => {
  return asset.vertical === 'materials'
}

export const isRenderCrateModel = (asset: { vertical: AssetKind }): asset is ThreeDAsset => {
  return asset.vertical === '3d'
}

export const isProductionCrateMisc = (asset: { vertical: AssetKind }): asset is MiscAsset => {
  return asset.vertical === 'misc'
}

export const isProductionCrateTutorial = (asset: { vertical: AssetKind }): asset is TutorialAsset => {
  return asset.vertical === 'tutorials'
}

/**
 * Gets a resolution label by its dimensions.
 */
export function getResolutionLabelByDimensions(
  dimensions: string,
  { appendDimensions, originalDimensions }: GetResolutionLabelParams = {
    appendDimensions: false,
  }
): ResolutionLabel {
  const resolution = getResolutionByDimensions(dimensions)

  if (!resolution) {
    return dimensions
  }

  if (appendDimensions) {
    // If resolution dimensions are not in the format of width x height,
    // we need to get the lower dimensions from the original dimensions
    let resolvedDimensions = ''

    // When dimensions and name don't match, it means the resolution is in the format of width x height
    if (resolution.dimensions !== resolution.name) {
      resolvedDimensions = resolution.dimensions
    } else {
      const lowerResolutions =
        appendDimensions && originalDimensions ? getLowerResolutions(originalDimensions) : undefined

      if (lowerResolutions) {
        const resolutionDimensions = lowerResolutions[resolution.id]

        if (resolutionDimensions) {
          resolvedDimensions = `${resolutionDimensions.width}x${resolutionDimensions.height}`
        }
      }
    }

    resolution.name += resolvedDimensions ? ` (${resolvedDimensions})` : ''
  }

  return resolution.name
}

/**
 * Standard resolution values
 */
export const resolutionsMap: Record<keyof Resolutions, { width: number; height: number }> = {
  '8k': { width: 7680, height: 4320 },
  '7k': { width: 7168, height: 3072 },
  '6k': { width: 6144, height: 3072 },
  '5k': { width: 5120, height: 2880 },
  '4k': { width: 3840, height: 2160 },
  '3k': { width: 3072, height: 1728 },
  '2k': { width: 2048, height: 1080 },
  '1k': { width: 1024, height: 540 },
  hd: { width: 1920, height: 1080 },
  sd: { width: 720, height: 480 },
  lq: { width: 320, height: 240 },
}

/**
 * Gets a resolution by its dimensions in the format of width x height.
 * If the resolution is not in the format of width x height, it will return the resolution name.
 */
export function getResolutionByDimensions(dimensions: string): Resolution | null {
  // Id dimensions does not contain "x", return null
  if (!dimensions.includes('x')) {
    return null
  }

  // Split dimensions and cast to number
  const [width, height] = dimensions.split('x').map(Number)

  // Find the resolution
  const findResolution = (dimensions: string) => {
    // Resolution is not in the format of width x height
    if (isNaN(width) || isNaN(height)) {
      return {
        id: dimensions.toLowerCase(),
        slug: dimensions.toLowerCase(),
        name: dimensions,
        dimensions,
      }
    }

    // 8K
    if (width >= 7680 /* && height >= 4320*/) {
      return {
        id: '8k',
        slug: '8k',
        name: '8K',
        dimensions,
      }
    }

    // 7K
    if (width >= 7168 /* && height >= 3072*/) {
      return {
        id: '7k',
        slug: '7k',
        name: '7K',
        dimensions,
      }
    }

    // 6K
    if (width >= 6144 /* && height >= 3072*/) {
      return {
        id: '6k',
        slug: '6k',
        name: '6K',
        dimensions,
      }
    }

    // 5K
    if (width >= 5120 /* && height >= 2880*/) {
      return {
        id: '5k',
        slug: '5k',
        name: '5K',
        dimensions,
      }
    }

    // 4k
    if (width >= 3840 /* && height >= 2160*/) {
      return {
        id: '4k',
        slug: '4k',
        name: '4K',
        dimensions,
      }
    }

    // 3k
    if (width >= 3072 /* && height >= 1728*/) {
      return {
        id: '3k',
        slug: '3k',
        name: '3K',
        dimensions,
      }
    }

    // 2K
    if (width >= 2048 /* && height >= 1080*/) {
      return {
        id: '2k',
        slug: '2k',
        name: '2K',
        dimensions,
      }
    }

    // HD
    if (width >= 1920 /* && height >= 1080*/) {
      return {
        id: 'hd',
        slug: 'hd',
        name: 'HD',
        dimensions,
      }
    }

    // 1K
    if (width >= 1024 /* && height >= 540*/) {
      return {
        id: '1k',
        slug: '1k',
        name: '1K',
        dimensions,
      }
    }

    // SD
    if (width >= 720 /* && height >= 480*/) {
      return {
        id: 'sd',
        slug: 'sd',
        name: 'SD',
        dimensions,
      }
    }

    // Default
    return {
      id: dimensions,
      slug: dimensions,
      name: dimensions,
      dimensions,
    }
  }

  return findResolution(dimensions)
}

/**
 * Resolve the logical format key for an asset file — the value used to store and restore download preferences.
 * "prores" and "png sequence" / "png-sequence" variations take priority because their extension ("mov" / "zip") is ambiguous.
 */
export function resolveFileFormat(file: Asset['files'][number]): string | null {
  const variationFormat = file.properties?.variations?.format ?? null
  if (variationFormat && ['prores', 'png sequence', 'png-sequence'].includes(variationFormat.toLowerCase())) {
    return variationFormat
  }
  return file.properties?.format ?? file.properties?.extension ?? variationFormat
}

/**
 * Get a user-friendly format label.
 * E.g. H.264 -> MP4
 * @param format
 * @returns string
 */
export function getFormatLabel(format: string): string {
  if (format === 'H.264') {
    return 'MP4'
  }

  if (format.toUpperCase() === 'PRORES') {
    return 'ProRes'
  }

  return format.toUpperCase()
}

/**
 * Label for a file's resolution option. Core's `resolution_label` wins and, like a legacy name
 * such as "HD", gets no "(WxH)" suffix. Without it the raw resolution goes through
 * `getResolutionLabelByDimensions` as before.
 */
export function getFileResolutionLabel(file: AssetFile, params?: GetResolutionLabelParams): ResolutionLabel | null {
  const variations = file.properties?.variations
  if (!variations || !('resolution' in variations) || !variations.resolution) {
    return null
  }

  return (
    ('resolution_label' in variations ? variations.resolution_label : null) ||
    getResolutionLabelByDimensions(variations.resolution, params)
  )
}

/**
 * Label for a file's format option. Core's `format_label` wins; without it the given format key
 * goes through `getFormatLabel` as before.
 */
export function getFileFormatLabel(file: AssetFile, format: string): string {
  return file.properties?.variations?.format_label || getFormatLabel(format)
}

/**
 * Given an asset resolution in the format of width x height, returns lower resolutions.
 * @param resolution
 */
export function getLowerResolutions(resolution: string): Record<ResolutionId, { width: number; height: number }> {
  const [originalWith, originalHeight] = resolution.split('x').map(Number)

  const lowerResolutions: Record<ResolutionId, { width: number; height: number }> = {}

  // Iterate resolutionsMap
  for (const [key, value] of Object.entries(resolutionsMap)) {
    const { width, height } = value

    // Skip if the evaluated resolution is higher than the original resolution
    if (width > originalWith || height > originalHeight) {
      continue
    }

    const isFirstMatchedResolution = Object.keys(lowerResolutions).length === 0

    if (isFirstMatchedResolution) {
      lowerResolutions[key as ResolutionId] = {
        width: originalWith,
        height: originalHeight,
      }
      continue
    }

    // Keep the size within the largest dimension
    const largestOriginalDimension = Math.max(originalWith, originalHeight)

    const widthRatio = width / largestOriginalDimension
    const heightRatio = height / largestOriginalDimension

    const maxRatio = Math.max(widthRatio, heightRatio)

    lowerResolutions[key as ResolutionId] = {
      width: Math.round(originalWith * maxRatio),
      height: Math.round(originalHeight * maxRatio),
    }
  }

  return lowerResolutions
}

/**
 * Get the key of an asset.
 * Used as asset slug, and also for asset cache key and favorite key.
 * @param asset
 */
export function getAssetKey(asset: Asset | AssetSummary): string {
  return asset.uuid
}

/**
 * Get file download URL.
 * @param file
 * @param userToken
 */
export function getFileDownloadUrl({ file }: { file: { uuid: string } }): string {
  return `${CRATE_CORE_API_BASE_URL}/assets/download/${file.uuid}`
}

export function getQueueUrl({ file, queueType }: { file: { uuid: string }; queueType: 'blender' | 'portal' }): string {
  return `${CRATE_CORE_API_BASE_URL}/assets/queue/${queueType}/${file.uuid}`
}

export function getDetailPath(asset: Asset | AssetSummary): string | null {
  const slug = asset.slug?.trim()

  if (!slug) {
    console.warn('Asset detail path not found for asset without slug', asset.uuid)
    return null
  }

  let basePath = ASSET_URL_TYPES[asset.type as keyof typeof ASSET_URL_TYPES]

  if (asset.type === 'sc_sounds') {
    const isMusic = 'is_music' in asset.properties && Boolean(asset.properties.is_music)
    basePath = isMusic ? ASSET_URL_TYPES.sc_sounds[1] : ASSET_URL_TYPES.sc_sounds[0]
  }

  if (Array.isArray(basePath)) {
    basePath = basePath[0]
  }

  if (!basePath) {
    console.warn('Asset detail path not found for', asset.uuid)
    return null
  }

  return `${basePath}/${slug}`
}

export function getDetailUrl(asset: Asset | AssetSummary): string | null {
  const detailPath = getDetailPath(asset)
  return detailPath ? new URL(detailPath, baseURL).toString() : null
}

/**
 * Build preview URL from an AssetPreview object.
 * @param preview
 * @returns string
 */
export function buildPreviewUrl(preview: AssetPreview): string {
  const host = preview.host ? preview.host.replace(/\/+$/, '') : 'https://p.productioncrate.com'
  return `${host}/${preview.path.replace(/^\/+/, '')}`
}

const VFX_GRID_VIDEO_VARIANT_B = '192x108'
const VFX_GRID_VIDEO_SUFFIX_B = '-extra_prev_b.mp4'
const VFX_GRID_VIDEO_VARIANT_C = '278x156'
const VFX_GRID_VIDEO_SUFFIX_C = '-extra_prev_c.mp4'

const normalizePreviewVariant = (variant?: string | null) => {
  if (!variant) {
    return ''
  }

  return variant.trim().toLowerCase().replace(/\s+/g, '')
}

const hasMp4Path = (path: string) => {
  return /\.mp4($|\?)/i.test(path)
}

const matchesVfxGridVideoPreview = (preview: AssetPreview, variant: string, suffix: string) => {
  const normalizedVariant = normalizePreviewVariant(preview.variant)
  if (normalizedVariant === variant) {
    return true
  }

  return preview.path.toLowerCase().includes(suffix)
}

/**
 * Get VFX grid video preview if applies.
 * Picks preferred preview between "b" and "c", then falls back to the other one.
 */
export function getVfxGridVideoPreview(asset: Asset | AssetSummary, preferredVariant: 'b' | 'c' = 'c'): string | null {
  if (!isFootageCrateEffect(asset)) {
    return null
  }

  const isVideoPreview = (preview: AssetPreview) => preview.type === 'video' && hasMp4Path(preview.path)

  const variantMatchers =
    preferredVariant === 'b'
      ? [
          { variant: VFX_GRID_VIDEO_VARIANT_B, suffix: VFX_GRID_VIDEO_SUFFIX_B },
          { variant: VFX_GRID_VIDEO_VARIANT_C, suffix: VFX_GRID_VIDEO_SUFFIX_C },
        ]
      : [
          { variant: VFX_GRID_VIDEO_VARIANT_C, suffix: VFX_GRID_VIDEO_SUFFIX_C },
          { variant: VFX_GRID_VIDEO_VARIANT_B, suffix: VFX_GRID_VIDEO_SUFFIX_B },
        ]

  for (const matcher of variantMatchers) {
    const matchedPreview = asset.previews.find(
      (preview) => isVideoPreview(preview) && matchesVfxGridVideoPreview(preview, matcher.variant, matcher.suffix)
    )

    if (matchedPreview) {
      return buildPreviewUrl(matchedPreview)
    }
  }

  return null
}

/** Select the smallest advertised MP4 without loading asset details or media files. */
export function getSmallestVideoPreview(
  asset: Asset | AssetSummary,
  excludedUrls: readonly string[] = []
): string | null {
  const sizes: Record<string, number> = { xs: 160 * 90, sm: 640 * 360, md: 1280 * 720, lg: 1920 * 1080 }
  let selected: AssetPreview | undefined
  let smallest = Infinity

  for (const preview of asset.previews) {
    if (preview.type !== 'video' || !hasMp4Path(preview.path) || excludedUrls.includes(buildPreviewUrl(preview)))
      continue
    const dimensions = /(\d+)\s*[x×]\s*(\d+)/i.exec(preview.variant)
    const area = dimensions ? Number(dimensions[1]) * Number(dimensions[2]) : (sizes[preview.variant] ?? Infinity)
    if (!selected || area < smallest) {
      selected = preview
      smallest = area
    }
  }

  return selected ? buildPreviewUrl(selected) : null
}

/**
 * Get asset image preview if applies.
 */
export function getThumbnail(asset: Asset | AssetSummary): string | null {
  const preview = asset.previews.find(
    (p) =>
      p.type === 'image' && (p.variant === '2x' || p.variant === 'xs' || p.variant === 'sm' || p.variant === 'front')
  )

  if (!preview) {
    return null
  }

  return buildPreviewUrl(preview)
}

/**
 * Get asset image preview if applies.
 */
export function getImagePreview(asset: Asset | AssetSummary): string | null {
  const preview = asset.previews.find(
    (p) =>
      p.type === 'image' && (p.variant === '2x' || p.variant === 'sm' || p.variant === 'lg' || p.variant === 'front')
  )

  if (!preview) {
    return null
  }

  return buildPreviewUrl(preview)
}

export function getWaveformPreview(asset: Asset | AssetSummary): string | null {
  if (!isSoundsCrateSound(asset)) {
    return null
  }

  const preview = asset.previews.find((p) => p.type === 'json' && p.variant === 'waveform')

  if (!preview) {
    return null
  }

  return buildPreviewUrl(preview)
}

/**
 * Get asset video preview if applies.
 */
export function getVideoPreview(asset: Asset | AssetSummary): string | null {
  if (isFootageCrateEffect(asset)) {
    const preview = asset.previews.find((p) => p.type === 'video' && p.variant === 'sm')

    if (!preview) {
      return null
    }

    return buildPreviewUrl(preview)
  }

  return null
}

/**
 * Get asset 360 preview if applies.
 */
export function get360Preview(asset: Asset | AssetSummary): string | null {
  if (isRenderCrateModel(asset)) {
    const preview = asset.previews.find((p) => p.type === 'image' && p.variant === 'sprite')

    if (!preview) {
      return null
    }

    return buildPreviewUrl(preview)
  }

  return null
}

/**
 * Get asset panorama preview if applies.
 */
export function getPanoramaPreview(asset: Asset | AssetSummary): string | null {
  if (isRenderCrateEnvironment(asset)) {
    const preview = asset.previews.find((p) => p.type === 'image' && p.variant === 'panorama')

    if (!preview) {
      return null
    }

    return buildPreviewUrl(preview)
  }

  return null
}

/**
 * Get asset audio preview if applies.
 */
export function getAudioPreview(asset: Asset | AssetSummary): string | null {
  if (isSoundsCrateSound(asset)) {
    return getAssetKey(asset)
  }

  return null
}

export function getAudioPreviewUrl(asset: Asset | AssetSummary): string | null {
  const preview = asset.previews.find((preview) => preview.type === 'audio' && preview.variant === 'mp3')
  return preview ? buildPreviewUrl(preview) : null
}

/**
 * Get asset type by vertical
 */
export function getAssetTypeByVertical(vertical: AssetKind): AssetType {
  switch (vertical) {
    case 'videos':
      return 'fc_effects'
    case 'graphics':
      return 'gc_images'
    case 'sounds':
      return 'sc_sounds'
    case '3d':
      return 'rc_objects'
    case 'environments':
      return 'rc_environments'
    case 'materials':
      return 'rc_materials'
    default:
      throw new Error(`Unknown asset vertical: ${vertical}`)
  }
}
